import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { Department, CreateDepartmentRequest } from '../types';
import { Building2, Plus, Edit2, Trash2, CheckCircle, XCircle, ChevronDown, ChevronUp, Users as UsersIcon, User, Mail, Phone, Key } from 'lucide-react';

export const Departments: React.FC = () => {
  const { isAdmin, agent } = useAuth();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState<Department | null>(null);
  const [formData, setFormData] = useState<CreateDepartmentRequest>({
    name: '',
    description: '',
    menuOption: '',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    adminPhone: '',
  });
  const [managerMode, setManagerMode] = useState<'create' | 'edit'>('create');
  const [selectedManagerId, setSelectedManagerId] = useState<string | null>(null);
  const [togglingDepartment, setTogglingDepartment] = useState<string | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultModalData, setResultModalData] = useState<{
    type: 'success' | 'error';
    title: string;
    message: string;
  } | null>(null);

  const toggleDepartmentExpand = (deptId: string) => {
    setExpandedDepartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deptId)) {
        newSet.delete(deptId);
      } else {
        newSet.add(deptId);
      }
      return newSet;
    });
  };

  const getRoleBadge = (role: string) => {
    const colors = {
      ADMIN: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
      GENERAL_MANAGER: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
      DEPARTMENT_MANAGER: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
      AGENT: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    };

    const labels = {
      ADMIN: 'Admin',
      GENERAL_MANAGER: 'Gerente Geral',
      DEPARTMENT_MANAGER: 'Gerente',
      AGENT: 'Agente',
    };

    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[role as keyof typeof colors]}`}>
        {labels[role as keyof typeof labels]}
      </span>
    );
  };

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      const data = await api.getDepartments();
      setDepartments(data);
    } catch (error) {
      console.error('Failed to load departments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (department?: Department) => {
    if (department) {
      setEditingDepartment(department);

      // Check if department has managers
      const hasManagers = department.users && department.users.length > 0;

      if (hasManagers) {
        // Default to "create" mode when opening
        setManagerMode('create');
        setSelectedManagerId(null);
        setFormData({
          name: department.name,
          description: department.description || '',
          menuOption: department.menuOption,
          adminName: '',
          adminEmail: '',
          adminPassword: '',
          adminPhone: '',
        });
      } else {
        // No managers - default to create
        setManagerMode('create');
        setSelectedManagerId(null);
        setFormData({
          name: department.name,
          description: department.description || '',
          menuOption: department.menuOption,
          adminName: '',
          adminEmail: '',
          adminPassword: '',
          adminPhone: '',
        });
      }
    } else {
      setEditingDepartment(null);
      setManagerMode('create');
      setSelectedManagerId(null);
      setFormData({
        name: '',
        description: '',
        menuOption: '',
        adminName: '',
        adminEmail: '',
        adminPassword: '',
        adminPhone: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDepartment(null);
    setManagerMode('create');
    setSelectedManagerId(null);
    setFormData({
      name: '',
      description: '',
      menuOption: '',
      adminName: '',
      adminEmail: '',
      adminPassword: '',
      adminPhone: '',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingDepartment) {
        const updateData: any = {
          name: formData.name,
          description: formData.description,
          menuOption: formData.menuOption,
        };

        // Add admin fields if provided
        if (formData.adminName) updateData.adminName = formData.adminName;
        if (formData.adminEmail) updateData.adminEmail = formData.adminEmail;
        if (formData.adminPassword) updateData.adminPassword = formData.adminPassword;
        if (formData.adminPhone !== undefined) updateData.adminPhone = formData.adminPhone;

        // Determine if we should pass adminId
        // - In "edit" mode: pass the selectedManagerId
        // - In "create" mode: don't pass adminId (creates new manager)
        if (managerMode === 'edit' && selectedManagerId) {
          updateData.adminId = selectedManagerId;
        }

        await api.updateDepartment(editingDepartment.id, updateData);
      } else {
        await api.createDepartment(formData);
      }
      await loadDepartments();
      handleCloseModal();
      setResultModalData({
        type: 'success',
        title: 'Sucesso',
        message: editingDepartment
          ? 'Departamento atualizado com sucesso!'
          : 'Departamento criado com sucesso!'
      });
      setShowResultModal(true);
    } catch (error: any) {
      console.error('Failed to save department:', error);
      setResultModalData({
        type: 'error',
        title: 'Erro',
        message: error.response?.data?.error || 'Falha ao salvar departamento'
      });
      setShowResultModal(true);
    }
  };

  const handleToggleActive = async (department: Department) => {
    setTogglingDepartment(department.id);
    try {
      await api.updateDepartment(department.id, { isActive: !department.isActive });
      await loadDepartments();
      setResultModalData({
        type: 'success',
        title: 'Sucesso',
        message: `Departamento ${!department.isActive ? 'ativado' : 'desativado'} com sucesso!`
      });
      setShowResultModal(true);
    } catch (error: any) {
      console.error('Failed to toggle department:', error);
      setResultModalData({
        type: 'error',
        title: 'Erro',
        message: error.response?.data?.error || 'Falha ao atualizar departamento'
      });
      setShowResultModal(true);
    } finally {
      setTogglingDepartment(null);
    }
  };

  const handleDelete = async () => {
    if (!departmentToDelete) return;

    try {
      await api.deleteDepartment(departmentToDelete.id);
      await loadDepartments();
      setShowDeleteModal(false);
      setDepartmentToDelete(null);
      setResultModalData({
        type: 'success',
        title: 'Sucesso',
        message: 'Departamento excluído com sucesso!'
      });
      setShowResultModal(true);
    } catch (error: any) {
      console.error('Failed to delete department:', error);
      setShowDeleteModal(false);
      setDepartmentToDelete(null);
      setResultModalData({
        type: 'error',
        title: 'Erro ao Excluir',
        message: error.response?.data?.error || 'Falha ao excluir departamento'
      });
      setShowResultModal(true);
    }
  };

  const openDeleteModal = (department: Department) => {
    setDepartmentToDelete(department);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDepartmentToDelete(null);
  };

  const filteredDepartments = departments.filter(dept => {
    // Hide "Administração do Sistema" from GENERAL_MANAGER
    if (agent?.role === 'GENERAL_MANAGER' && dept.name === 'Administração do Sistema') {
      return false;
    }

    if (statusFilter === 'active') return dept.isActive;
    if (statusFilter === 'inactive') return !dept.isActive;
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Departamentos</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Gerenciar departamentos de atendimento ao cliente</p>
        </div>
        {(isAdmin() || agent?.role === 'GENERAL_MANAGER') && (
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Adicionar Departamento</span>
          </button>
        )}
      </div>

      {/* Status Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Filtrar por status:
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          >
            <option value="all">Todos</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
          </select>
        </div>
      </div>

      {/* Departments Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDepartments.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
            <p className="text-gray-600 dark:text-gray-400">Nenhum departamento encontrado</p>
            <button
              onClick={() => handleOpenModal()}
              className="mt-4 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
            >
              Criar seu primeiro departamento
            </button>
          </div>
        ) : (
          filteredDepartments.map((department) => (
            <div
              key={department.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3 flex-1">
                  <div className={`p-2 rounded-lg ${
                    department.name === 'Administração do Sistema'
                      ? 'bg-purple-100 dark:bg-purple-900/30'
                      : department.name === 'Gerente de Departamentos'
                      ? 'bg-indigo-100 dark:bg-indigo-900/30'
                      : 'bg-blue-100 dark:bg-blue-900/30'
                  }`}>
                    <Building2 className={`w-6 h-6 ${
                      department.name === 'Administração do Sistema'
                        ? 'text-purple-600 dark:text-purple-400'
                        : department.name === 'Gerente de Departamentos'
                        ? 'text-indigo-600 dark:text-indigo-400'
                        : 'text-blue-600 dark:text-blue-400'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-semibold text-gray-900 dark:text-white">{department.name}</h3>
                      {department.name === 'Administração do Sistema' && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                          Administração do Sistema
                        </span>
                      )}
                      {department.name === 'Gerente de Departamentos' && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400">
                          Gerência Geral
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <span
                  className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                    department.isActive
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {department.isActive ? (
                    <>
                      <CheckCircle className="w-3 h-3" />
                      <span>Ativo</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-3 h-3" />
                      <span>Inativo</span>
                    </>
                  )}
                </span>
              </div>

              {department.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{department.description}</p>
              )}

              {department.name === 'Administração do Sistema' && (
                <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                  <p className="text-xs text-purple-700 dark:text-purple-400">
                    ℹ️ Usuários neste departamento possuem escopo de administrador do sistema
                  </p>
                </div>
              )}

              {department.name === 'Gerente de Departamentos' && (
                <div className="mb-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
                  <p className="text-xs text-indigo-700 dark:text-indigo-400">
                    ℹ️ Usuários neste departamento são gerentes gerais que supervisionam todos os departamentos
                  </p>
                </div>
              )}

              {/* Users List - Visible for Admin and General Manager */}
              {(isAdmin() || agent?.role === 'GENERAL_MANAGER') && department.users && department.users.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => toggleDepartmentExpand(department.id)}
                    className="flex items-center justify-between w-full text-left mb-2"
                  >
                    <div className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      <UsersIcon className="w-4 h-4" />
                      <span>{department.users.length} Usuário{department.users.length !== 1 ? 's' : ''}</span>
                    </div>
                    {expandedDepartments.has(department.id) ? (
                      <ChevronUp className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    )}
                  </button>

                  {expandedDepartments.has(department.id) && (
                    <div className="space-y-2 mt-2">
                      {department.users.map((user) => (
                        <div
                          key={user.id}
                          className="flex items-start justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <User className="w-3 h-3 text-gray-400 flex-shrink-0" />
                              <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {user.name}
                              </span>
                              {getRoleBadge(user.role)}
                            </div>
                            <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400 mb-1">
                              <Mail className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{user.email}</span>
                            </div>
                            {user.phone && (
                              <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400 mb-1">
                                <Phone className="w-3 h-3 flex-shrink-0" />
                                <span>{user.phone}</span>
                              </div>
                            )}
                            {user.plainPassword && (
                              <div className="flex items-center space-x-1 text-xs">
                                <Key className="w-3 h-3 flex-shrink-0 text-amber-500" />
                                <span className="font-mono bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded">
                                  {user.plainPassword}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center space-x-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                {(isAdmin() || (agent?.role === 'GENERAL_MANAGER' && department.name !== 'Administração do Sistema')) && (
                  <button
                    onClick={() => handleOpenModal(department)}
                    className="flex-1 px-3 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition flex items-center justify-center space-x-1"
                  >
                    <Edit2 className="w-4 h-4" />
                    <span>Editar</span>
                  </button>
                )}
                {(isAdmin() || agent?.role === 'GENERAL_MANAGER') && department.name !== 'Administração do Sistema' && department.name !== 'Gerente de Departamentos' && (
                  <>
                    <button
                      onClick={() => handleToggleActive(department)}
                      disabled={togglingDepartment === department.id}
                      className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      {togglingDepartment === department.id ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700 dark:border-gray-300"></div>
                          <span>Processando...</span>
                        </>
                      ) : (
                        <span>{department.isActive ? 'Desativar' : 'Ativar'}</span>
                      )}
                    </button>
                    <button
                      onClick={() => openDeleteModal(department)}
                      className="px-3 py-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingDepartment ? 'Editar Departamento' : 'Adicionar Departamento'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome do Departamento *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="ex: Vendas"
                />
              </div>

              {/* Only show Menu Option field when creating new department */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                  placeholder="Descrição opcional"
                />
              </div>

              {/* Admin fields - visible for admins, but not for "Gerente de Departamentos" */}
              {isAdmin() && editingDepartment?.name !== 'Gerente de Departamentos' && (() => {
                // Determine if this is the System Admin department
                const isSystemAdminDept = editingDepartment?.name === 'Administração do Sistema';
                const roleLabel = isSystemAdminDept ? 'Admin' : 'Gerente';
                const roleLabelPlural = isSystemAdminDept ? 'Admins' : 'Gerentes';

                return (
                  <>
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        {editingDepartment
                          ? `Gerenciar ${roleLabelPlural}`
                          : 'Gerente do Departamento (Opcional)'}
                      </h3>

                    {/* Mode Selection - Only show when editing department with managers */}
                    {editingDepartment && editingDepartment.users && editingDepartment.users.length > 0 && (
                      <div className="mb-4 space-y-3">
                        {/* Radio buttons for mode selection */}
                        <div className="flex flex-col space-y-2">
                          <label className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 ${managerMode === 'create' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700'}`}>
                            <input
                              type="radio"
                              name="managerMode"
                              checked={managerMode === 'create'}
                              onChange={() => {
                                setManagerMode('create');
                                setSelectedManagerId(null);
                                setFormData({
                                  ...formData,
                                  adminName: '',
                                  adminEmail: '',
                                  adminPassword: '',
                                  adminPhone: '',
                                });
                              }}
                              className="w-4 h-4 text-blue-600"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-white">Adicionar Novo {roleLabel}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Criar um novo {roleLabel.toLowerCase()} para este departamento</div>
                            </div>
                          </label>

                          <label className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 ${managerMode === 'edit' ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-gray-200 dark:border-gray-700'}`}>
                            <input
                              type="radio"
                              name="managerMode"
                              checked={managerMode === 'edit'}
                              onChange={() => {
                                setManagerMode('edit');
                                // Clear form until user selects a manager
                                setSelectedManagerId(null);
                                setFormData({
                                  ...formData,
                                  adminName: '',
                                  adminEmail: '',
                                  adminPassword: '',
                                  adminPhone: '',
                                });
                              }}
                              className="w-4 h-4 text-green-600"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-white">Editar {roleLabel} Existente</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Atualizar dados de um {roleLabel.toLowerCase()} atual</div>
                            </div>
                          </label>
                        </div>

                        {/* Manager selector dropdown - only show in edit mode */}
                        {managerMode === 'edit' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Selecione o {roleLabel} *
                            </label>
                            <select
                              value={selectedManagerId || ''}
                              onChange={(e) => {
                                const managerId = e.target.value;
                                setSelectedManagerId(managerId);

                                // Find and populate form with selected manager's data
                                const manager = editingDepartment.users?.find(u => u.id === managerId);
                                if (manager) {
                                  setFormData({
                                    ...formData,
                                    adminName: manager.name,
                                    adminEmail: manager.email,
                                    adminPassword: '',
                                    adminPhone: manager.phone || '',
                                  });
                                }
                              }}
                              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
                              required
                            >
                              <option value="">Selecione um {roleLabel.toLowerCase()}...</option>
                              {editingDepartment.users?.map(user => (
                                <option key={user.id} value={user.id}>
                                  {user.name} ({user.email})
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}

                      {/* Info text */}
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                        {!editingDepartment && 'Criar um gerente para este departamento'}
                        {editingDepartment && (!editingDepartment.users || editingDepartment.users.length === 0) && `Criar o primeiro ${roleLabel.toLowerCase()} deste departamento`}
                        {editingDepartment && editingDepartment.users && editingDepartment.users.length > 0 && managerMode === 'create' && `Preencha os dados para criar um novo ${roleLabel.toLowerCase()}. Os ${roleLabelPlural.toLowerCase()} atuais serão mantidos.`}
                        {editingDepartment && editingDepartment.users && editingDepartment.users.length > 0 && managerMode === 'edit' && selectedManagerId && `Atualize as informações do ${roleLabel.toLowerCase()} selecionado.`}
                        {editingDepartment && editingDepartment.users && editingDepartment.users.length > 0 && managerMode === 'edit' && !selectedManagerId && `Selecione um ${roleLabel.toLowerCase()} acima para editar.`}
                      </p>

                      {/* Manager form fields - disabled in edit mode until manager is selected */}
                      {(!editingDepartment || !editingDepartment.users || editingDepartment.users.length === 0 || managerMode === 'create' || (managerMode === 'edit' && selectedManagerId)) && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Nome do Gerente
                        </label>
                        <input
                          type="text"
                          value={formData.adminName}
                          onChange={(e) => setFormData({ ...formData, adminName: e.target.value })}
                          disabled={managerMode === 'edit' && !selectedManagerId}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="ex: João Silva"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Email do Gerente
                        </label>
                        <input
                          type="email"
                          value={formData.adminEmail}
                          onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                          disabled={managerMode === 'edit' && !selectedManagerId}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="ex: joao@example.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Telefone do Gerente
                        </label>
                        <input
                          type="tel"
                          value={formData.adminPhone}
                          onChange={(e) => setFormData({ ...formData, adminPhone: e.target.value })}
                          disabled={managerMode === 'edit' && !selectedManagerId}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="ex: (11) 99999-9999"
                        />
                      </div>

                      {/* Only show password field if not editing an ADMIN manager */}
                      {(() => {
                        const selectedManager = editingDepartment?.users?.find(u => u.id === selectedManagerId);
                        const isAdminManager = selectedManager?.role === 'ADMIN';

                        if (managerMode === 'edit' && isAdminManager) {
                          return (
                            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
                              <p className="text-sm text-purple-700 dark:text-purple-400">
                                ℹ️ A senha de administradores do sistema não pode ser alterada aqui
                              </p>
                            </div>
                          );
                        }

                        return (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Senha do Gerente {managerMode === 'edit' && selectedManagerId && '(deixe em branco para manter a atual)'}
                            </label>
                            <input
                              type="password"
                              value={formData.adminPassword}
                              onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })}
                              disabled={managerMode === 'edit' && !selectedManagerId}
                              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                              placeholder={managerMode === 'edit' && selectedManagerId ? 'Deixe em branco para não alterar' : 'Mínimo 6 caracteres'}
                              minLength={6}
                            />
                          </div>
                        );
                      })()}
                      </>
                    )}
                    </div>
                  </>
                );
              })()}
              </div>

              <div className="flex items-center space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition"
                >
                  {editingDepartment ? 'Atualizar' : 'Criar'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && departmentToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Confirmar Exclusão
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              Tem certeza que deseja excluir o departamento <span className="font-semibold text-gray-900 dark:text-white">{departmentToDelete.name}</span>?
            </p>
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-2 space-y-1">
              <p>• Os agentes deste departamento ficarão sem departamento</p>
              <p>• As conversas ficarão desvinculadas (histórico preservado)</p>
            </div>
            <p className="text-sm text-red-600 dark:text-red-400 mb-6">
              ⚠️ Esta ação não pode ser desfeita!
            </p>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white rounded-lg transition"
              >
                Excluir
              </button>
              <button
                onClick={closeDeleteModal}
                className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result Modal */}
      {showResultModal && resultModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              {resultModalData.type === 'success' ? (
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mr-4">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
              ) : (
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mr-4">
                  <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
              )}
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {resultModalData.title}
              </h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {resultModalData.message}
            </p>
            <button
              onClick={() => {
                setShowResultModal(false);
                setResultModalData(null);
              }}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
